export const FLOW_COLOR_TOKENS = [
  'blue',
  'teal',
  'green',
  'amber',
  'orange',
  'rose',
  'purple',
  'slate'
] as const

export type FlowColorToken = (typeof FLOW_COLOR_TOKENS)[number]

export function normalizeFlowColor(value: string): string {
  return value.trim().toLowerCase()
}

export function isAllowedFlowColor(value: string | undefined): value is FlowColorToken {
  if (!value) return false
  return (FLOW_COLOR_TOKENS as readonly string[]).includes(normalizeFlowColor(value))
}

export function getRandomFlowColor(): FlowColorToken {
  const idx = Math.floor(Math.random() * FLOW_COLOR_TOKENS.length)
  return FLOW_COLOR_TOKENS[idx]
}

/**
 * Deterministic fallback color derived from flowId.
 * Uses a simple hash so legacy flows (without a persisted color) always
 * resolve to the same token — matching the backend's SHA256-based derivation.
 */
export function deriveFlowColor(flowId: string): FlowColorToken {
  let hash = 0
  for (let i = 0; i < flowId.length; i++) {
    hash = ((hash << 5) - hash + flowId.charCodeAt(i)) | 0
  }
  const idx = Math.abs(hash) % FLOW_COLOR_TOKENS.length
  return FLOW_COLOR_TOKENS[idx]
}

/**
 * Return the flow's persisted color, or derive one deterministically from the id.
 * Use this instead of getRandomFlowColor() whenever displaying an existing flow.
 */
export function resolveFlowColor(flow: { id: string; color?: string }): FlowColorToken {
  if (flow.color && isAllowedFlowColor(flow.color)) return normalizeFlowColor(flow.color) as FlowColorToken
  return deriveFlowColor(flow.id)
}
